/**
 * IndexedDB Manager for 1946 Billing PWA
 * Handles local storage for offline functionality
 */

const DB_NAME = 'billing_1946';
const DB_VERSION = 1;

class LocalDB {
  constructor() {
    this.db = null;
  }

  /**
   * Initialize/Open IndexedDB
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[IndexedDB] Error opening database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[IndexedDB] Database opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        console.log('[IndexedDB] Database upgrade needed');

        // Create object stores (tables)
        
        // 1. Menu Items - stores all menu items
        if (!db.objectStoreNames.contains('menu_items')) {
          const menuStore = db.createObjectStore('menu_items', { keyPath: 'id' });
          menuStore.createIndex('category', 'category', { unique: false });
          console.log('[IndexedDB] Created menu_items store');
        }

        // 2. Franchise Info - stores current franchise data
        if (!db.objectStoreNames.contains('franchise_info')) {
          db.createObjectStore('franchise_info', { keyPath: 'id' });
          console.log('[IndexedDB] Created franchise_info store');
        }

        // 3. Ledger Parties - stores all party ledger entries
        if (!db.objectStoreNames.contains('ledger_parties')) {
          const ledgerStore = db.createObjectStore('ledger_parties', { keyPath: 'party_id' });
          ledgerStore.createIndex('franchise_id', 'franchise_id', { unique: false });
          console.log('[IndexedDB] Created ledger_parties store');
        }

        // 4. Pending Orders - orders waiting to sync
        if (!db.objectStoreNames.contains('pending_orders')) {
          const pendingOrdersStore = db.createObjectStore('pending_orders', { keyPath: 'order_no' });
          pendingOrdersStore.createIndex('created_at', 'created_at', { unique: false });
          pendingOrdersStore.createIndex('franchise_id', 'franchise_id', { unique: false });
          console.log('[IndexedDB] Created pending_orders store');
        }

        // 5. Synced Orders - last 100 orders for history
        if (!db.objectStoreNames.contains('synced_orders')) {
          const syncedOrdersStore = db.createObjectStore('synced_orders', { keyPath: 'order_no' });
          syncedOrdersStore.createIndex('date_timestamp', 'date_timestamp', { unique: false });
          console.log('[IndexedDB] Created synced_orders store');
        }

        // 6. Pending Ledger Transactions - ledger transactions to sync
        if (!db.objectStoreNames.contains('pending_ledger')) {
          const pendingLedgerStore = db.createObjectStore('pending_ledger', { keyPath: 'transaction_id' });
          pendingLedgerStore.createIndex('party_id', 'party_id', { unique: false });
          pendingLedgerStore.createIndex('created_at', 'created_at', { unique: false });
          console.log('[IndexedDB] Created pending_ledger store');
        }

        // 7. Sync Metadata - tracks last sync time, counts, etc.
        if (!db.objectStoreNames.contains('sync_metadata')) {
          db.createObjectStore('sync_metadata', { keyPath: 'key' });
          console.log('[IndexedDB] Created sync_metadata store');
        }
      };
    });
  }

  /**
   * Generic get operation
   */
  async get(storeName, key) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Generic getAll operation
   */
  async getAll(storeName) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Generic put operation (add or update)
   */
  async put(storeName, data) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Generic delete operation
   */
  async delete(storeName, key) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all data from a store
   */
  async clear(storeName) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get items by index
   */
  async getByIndex(storeName, indexName, value) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(value);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Bulk put operation
   */
  async bulkPut(storeName, items) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      let completed = 0;
      const total = items.length;

      items.forEach(item => {
        const request = store.put(item);
        request.onsuccess = () => {
          completed++;
          if (completed === total) {
            resolve(completed);
          }
        };
        request.onerror = () => reject(request.error);
      });

      if (total === 0) resolve(0);
    });
  }

  /**
   * Count items in store
   */
  async count(storeName) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
const localDB = new LocalDB();
export default localDB;
